import { useState, useCallback, useEffect, useRef } from 'react';
import { Plug, CheckCircle, XCircle, Clock, Server, Wifi, WifiOff } from 'lucide-react';
import type { ToolComponentProps } from './index';

interface PortCheckResult {
  host: string;
  port: number;
  isOpen: boolean;
  responseTime: number | null;
  service: string | null;
  banner: string | null;
  error: string | null;
}

interface MultiPortResult {
  host: string;
  timestamp: string;
  results: PortCheckResult[];
  summary: {
    total: number;
    open: number;
    closed: number;
  };
}

// Common ports for quick scan
const COMMON_PORTS = [
  { port: 21, name: 'FTP' },
  { port: 22, name: 'SSH' },
  { port: 23, name: 'Telnet' },
  { port: 25, name: 'SMTP' },
  { port: 53, name: 'DNS' },
  { port: 80, name: 'HTTP' },
  { port: 110, name: 'POP3' },
  { port: 143, name: 'IMAP' },
  { port: 443, name: 'HTTPS' },
  { port: 465, name: 'SMTPS' },
  { port: 587, name: 'Submission' },
  { port: 993, name: 'IMAPS' },
  { port: 995, name: 'POP3S' },
  { port: 3306, name: 'MySQL' },
  { port: 3389, name: 'RDP' },
  { port: 5432, name: 'PostgreSQL' },
  { port: 6379, name: 'Redis' },
  { port: 8080, name: 'HTTP Alt' },
  { port: 8443, name: 'HTTPS Alt' },
  { port: 27017, name: 'MongoDB' },
];

// In production, nginx serves both frontend and API from same origin
const API_BASE = '';

export function PortChecker({ initialValue, initialPort }: ToolComponentProps) {
  const [host, setHost] = useState(initialValue || '');
  const [port, setPort] = useState(initialPort || '443');
  const [result, setResult] = useState<PortCheckResult | null>(null);
  const [multiResult, setMultiResult] = useState<MultiPortResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'single' | 'multi'>('single');
  const hasAutoTriggered = useRef(false);

  const handleSingleCheck = useCallback(async () => {
    let hostname = host.trim();
    if (!hostname) {
      setError('Please enter a hostname or IP address');
      return;
    }

    // Strip protocol if provided
    hostname = hostname.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];

    const portNum = parseInt(port, 10);
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      setError('Port must be between 1 and 65535');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setMultiResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/port/${encodeURIComponent(hostname)}?port=${portNum}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Port check failed');
        return;
      }

      setResult(data);

      // Update browser URL for sharing
      const params = new URLSearchParams();
      params.set('host', hostname);
      params.set('port', portNum.toString());
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } catch (err) {
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [host, port]);

  const handleMultiScan = useCallback(async () => {
    let hostname = host.trim();
    if (!hostname) {
      setError('Please enter a hostname or IP address');
      return;
    }

    // Strip protocol if provided
    hostname = hostname.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];

    setIsLoading(true);
    setError(null);
    setResult(null);
    setMultiResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/port/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host: hostname,
          ports: COMMON_PORTS.map(p => p.port),
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Port scan failed');
        return;
      }

      setMultiResult(data);

      // Update browser URL for sharing
      const params = new URLSearchParams();
      params.set('host', hostname);
      params.set('scan', 'multi');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } catch (err) {
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [host]);

  const handleCheck = useCallback(() => {
    if (mode === 'single') {
      handleSingleCheck();
    } else {
      handleMultiScan();
    }
  }, [mode, handleSingleCheck, handleMultiScan]);

  // Auto-trigger check when initialValue is provided via URL
  useEffect(() => {
    if (initialValue && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handleCheck();
    }
  }, [initialValue, handleCheck]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCheck();
    }
  };

  const renderSingleResult = (res: PortCheckResult) => (
    <div className="space-y-4">
      {/* Status Summary */}
      <div className={`flex items-center gap-4 p-4 rounded-lg ${res.isOpen ? 'bg-[var(--accent-dim)]' : 'bg-red-500/10'}`}>
        {res.isOpen ? (
          <Wifi className="w-10 h-10 text-[var(--accent)]" />
        ) : (
          <WifiOff className="w-10 h-10 text-red-400" />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className={`font-semibold text-lg ${res.isOpen ? 'text-[var(--accent)]' : 'text-red-400'}`}>
              Port {res.port} is {res.isOpen ? 'Open' : 'Closed'}
            </span>
            {res.service && (
              <span className="px-2 py-0.5 text-sm font-medium rounded bg-[var(--bg-tertiary)] text-[var(--accent)]">
                {res.service}
              </span>
            )}
          </div>
          <div className="text-sm text-[var(--text-muted)] mt-1">
            {res.host}:{res.port}
            {res.responseTime !== null && (
              <span className="ml-3 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {res.responseTime}ms
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 text-sm bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
        <div className="grid grid-cols-[120px_1fr] gap-2">
          <span className="text-[var(--text-muted)]">Host:</span>
          <span className="font-mono">{res.host}</span>
        </div>
        <div className="grid grid-cols-[120px_1fr] gap-2">
          <span className="text-[var(--text-muted)]">Port:</span>
          <span className="font-mono">{res.port}</span>
        </div>
        <div className="grid grid-cols-[120px_1fr] gap-2">
          <span className="text-[var(--text-muted)]">Status:</span>
          <span className={res.isOpen ? 'text-[var(--accent)]' : 'text-red-400'}>
            {res.isOpen ? 'Open (Accepting connections)' : res.error || 'Closed'}
          </span>
        </div>
        {res.service && (
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Service:</span>
            <span>{res.service}</span>
          </div>
        )}
        {res.responseTime !== null && (
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Latency:</span>
            <span className="text-[var(--accent)]">{res.responseTime} ms</span>
          </div>
        )}
        {res.banner && (
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Banner:</span>
            <span className="font-mono text-xs bg-[var(--bg-tertiary)] p-2 rounded break-all">{res.banner}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderMultiResult = (res: MultiPortResult) => (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
        <Server className="w-10 h-10 text-[var(--accent)]" />
        <div className="flex-1">
          <div className="font-semibold text-lg text-[var(--text-primary)]">
            Scan Complete: {res.host}
          </div>
          <div className="flex gap-4 mt-1 text-sm">
            <span className="text-[var(--accent)]">
              {res.summary.open} open
            </span>
            <span className="text-red-400">
              {res.summary.closed} closed
            </span>
            <span className="text-[var(--text-muted)]">
              {res.summary.total} total
            </span>
          </div>
        </div>
      </div>

      {/* Port List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {res.results.map((portRes) => (
          <div
            key={portRes.port}
            className={`flex items-center gap-3 p-3 rounded-lg border ${
              portRes.isOpen
                ? 'bg-[var(--accent-dim)] border-[var(--accent)]/30'
                : 'bg-[var(--bg-secondary)] border-[var(--border-color)]'
            }`}
          >
            {portRes.isOpen ? (
              <CheckCircle className="w-5 h-5 text-[var(--accent)] shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{portRes.port}</span>
                {portRes.service && (
                  <span className="text-xs text-[var(--text-muted)]">
                    {portRes.service}
                  </span>
                )}
              </div>
              {portRes.responseTime !== null && (
                <span className="text-xs text-[var(--text-muted)]">
                  {portRes.responseTime}ms
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (result) {
      return renderSingleResult(result);
    }

    if (multiResult) {
      return renderMultiResult(multiResult);
    }

    return (
      <div className="text-[var(--text-muted)] text-center py-8">
        Enter a hostname or IP address above to check if a port is open and accepting connections.
      </div>
    );
  };

  return (
    <div className="space-y-6" onKeyDown={handleKeyDown}>
      {/* Mode Toggle */}
      <div className="flex gap-2 p-1 rounded-lg bg-[var(--bg-secondary)] w-fit">
        <button
          type="button"
          onClick={() => setMode('single')}
          className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
            mode === 'single'
              ? 'bg-[var(--accent)] text-black'
              : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
          }`}
        >
          Single Port
        </button>
        <button
          type="button"
          onClick={() => setMode('multi')}
          className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
            mode === 'multi'
              ? 'bg-[var(--accent)] text-black'
              : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
          }`}
        >
          Common Ports Scan
        </button>
      </div>

      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          {mode === 'single' ? 'Host & Port' : 'Host'}
        </label>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <input
            type="text"
            className="flex-1 min-w-0 px-3 sm:px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
            placeholder="example.com or 192.168.1.1"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            spellCheck={false}
          />
          <div className="flex gap-2 sm:gap-3">
            {mode === 'single' && (
              <input
                type="number"
                className="w-20 sm:w-24 px-3 sm:px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-center text-sm"
                placeholder="443"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                min={1}
                max={65535}
              />
            )}
            <button
              type="button"
              onClick={handleCheck}
              disabled={isLoading || !host.trim()}
              className="btn-primary flex items-center justify-center gap-2 flex-1 sm:flex-none px-4 sm:px-6 whitespace-nowrap"
            >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {mode === 'single' ? 'Checking...' : 'Scanning...'}
              </>
            ) : (
              <>
                <Plug className="w-4 h-4" />
                <span className="hidden sm:inline">{mode === 'single' ? 'Check Port' : 'Scan Ports'}</span>
                <span className="sm:hidden">{mode === 'single' ? 'Check' : 'Scan'}</span>
              </>
            )}
            </button>
          </div>
        </div>
      </div>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          {mode === 'single' ? 'Port Status' : 'Scan Results'}
        </label>
        <div className="output-panel">
          {renderOutput()}
        </div>
      </div>

      <div className="text-xs text-[var(--text-muted)]">
        <span className="text-[var(--accent)]">Note:</span> This tool performs a TCP connection test to determine if a port is open. 
        {mode === 'multi' && ' Common ports scanned: 21, 22, 23, 25, 53, 80, 110, 143, 443, 465, 587, 993, 995, 3306, 3389, 5432, 6379, 8080, 8443, 27017.'}
      </div>
    </div>
  );
}
